import {
  faFloppyDisk,
  faPenToSquare,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box, useMediaQuery, useTheme } from "@mui/system";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { CMSContext } from "../context/CaseManagmentContext";
import { useSelector } from "react-redux";

const CaseDetailCard = ({ title, data, onEdit, editable, onSave, loading }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [initialData, setInitialData] = useState({});
  const [formData, setFormData] = useState({});
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const ownerId = userInfo._id;
  const caseAdvocate = data?.advocates?.find(
    (advocate) => advocate.userId === ownerId
  );
  const isOwner = data?.createdBy === ownerId || caseAdvocate?.role === "Owner";
  const { fetchCases } = useContext(CMSContext);
  const allowedFields = [
    "caseTitle",
    "caseDescription",
    "filedBy",
    "startDate",
    "endDate",
    "status",
  ];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  useEffect(() => {
    if (isMobile) {
      fetchCases();
    }
  }, [ownerId]);

  const handleEditClick = () => setIsEditing(true);
  const handleSaveClick = () => {
    setIsEditing(false);
    onSave(formData);
  };

  // Ensure formData is formatted on component mount or when data changes
  useEffect(() => {
    if (data) {
      const filteredData = Object.entries(data).reduce((acc, [key, value]) => {
        if (allowedFields.includes(key)) {
          acc[key] =
            key.toLowerCase().includes("date") &&
            moment(value, moment.ISO_8601, true).isValid()
              ? moment(value).format("LL")
              : value;
        }
        return acc;
      }, {});
      setFormData(filteredData);
      setInitialData(filteredData);
    }
  }, [data]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Card
      sx={{
        borderRadius: "10px",
        p: 1,
        maxHeight: 390,
        minHeight: 390,

        "::-webkit-scrollbar": { width: "5px" },
        "::-webkit-scrollbar-track": { background: "#fff" },
        "::-webkit-scrollbar-thumb": {
          background: "#bbb",
          borderRadius: "6px",
        },
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            bgcolor: "#003D78",
            color: "#FFFFFF",
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            p: "4px 8px 4px 8px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography fontSize={"15px"}>{title}</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {isOwner && (
              <Tooltip title="Edit Case" arrow>
                <Button
                  size="small"
                  onClick={isEditing ? handleSaveClick : handleEditClick}
                  sx={{
                    bgcolor: "#FFFFFF",
                    minWidth: "25px",
                    minHeight: "25px",
                    padding: 0,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: 1,
                    "&:hover": {
                      bgcolor: "#f0f0f0",
                    },
                  }}
                >
                  <FontAwesomeIcon
                    icon={isEditing ? faFloppyDisk : faPenToSquare}
                    style={{ fontSize: "14px", color: "#003D78" }}
                  />
                </Button>
              </Tooltip>
            )}
            {isEditing && (
              <Button
                size="small"
                onClick={() => {
                  setIsEditing(false);
                  setFormData(initialData);
                }}
                sx={{
                  bgcolor: "#FFFFFF",
                  minWidth: "25px",
                  minHeight: "25px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: 1,
                  "&:hover": {
                    bgcolor: "#f0f0f0",
                  },
                }}
              >
                <FontAwesomeIcon
                  icon={faXmark}
                  style={{ fontSize: "14px", color: "#003D78" }}
                />
              </Button>
            )}
          </Box>
        </Box>

        {/* Content */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              height: "300px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            {/* Centered Case Title */}
            <Typography
              align="center"
              variant="subtitle1"
              fontWeight="bold"
              color="#003D78"
              gutterBottom
            >
              {formData.caseTitle}
            </Typography>

            {/* Centered Case Description */}
            <Tooltip title={formData.caseDescription} arrow>
              <Typography
                align="center"
                fontSize="14px"
                color="textSecondary"
                gutterBottom
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {formData.caseDescription}
              </Typography>
            </Tooltip>

            {/* Two-Column Fields */}
            <Box sx={{ mt: 2 }}>
              {Object.entries(formData).map(([key, value]) => {
                if (key === "caseTitle" || key === "caseDescription")
                  return null;

                const label = key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase());

                return (
                  <Box
                    key={key}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography
                      fontSize="14px"
                      fontWeight="500"
                      color="textSecondary"
                    >
                      {label}
                    </Typography>
                    {isEditing ? (
                      key === "filedBy" ? (
                        <Typography fontSize="14px">{value}</Typography>
                      ) : key === "status" ? (
                        <FormControl
                          sx={{ width: "60%" }}
                          fullWidth
                          size="small"
                        >
                          <Select
                            sx={{
                              fontSize: "13px",
                              "& .MuiSelect-select": {
                                padding: "8px 8px",
                              },
                            }}
                            value={value || ""}
                            onChange={(e) => handleChange(key, e.target.value)}
                          >
                            <MenuItem
                              sx={{ fontSize: "13px" }}
                              value="In Progress"
                            >
                              In Progress
                            </MenuItem>
                            <MenuItem
                              sx={{ fontSize: "13px" }}
                              value="Not Started"
                            >
                              Not Started
                            </MenuItem>
                            <MenuItem
                              sx={{ fontSize: "13px" }}
                              value="Completed"
                            >
                              Completed
                            </MenuItem>
                          </Select>
                        </FormControl>
                      ) : key === "startDate" || key === "endDate" ? (
                        <TextField
                          size="small"
                          type="date"
                          value={moment(value).format("YYYY-MM-DD")}
                          defaultValue={moment(value).format("YYYY-MM-DD")}
                          onChange={(e) => {
                            handleChange(key, e.target.value);
                          }}
                          sx={{ width: "60%" }}
                          InputProps={{
                            sx: {
                              bgcolor: "white",
                              fontSize: "13px",
                              "& input:-webkit-autofill": {
                                backgroundColor: "white !important",
                              },
                            },
                          }}
                        />
                      ) : null
                    ) : key === "status" ? (
                      <Chip
                        variant="outlined"
                        label={value}
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          borderRadius: "16px",
                          px: 1.5,
                          py: 0.5,
                          borderColor: "#1976d2",
                          color: "#1976d2",
                          "&:hover": {
                            backgroundColor: "rgba(25, 118, 210, 0.04)",
                          },
                        }}
                      />
                    ) : (
                      <Typography fontSize="14px">{value}</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
export default CaseDetailCard;
